import {
  type BoardCardAddress,
  type CellAddress,
  type ItemId,
  useBoardCell,
  useBoardCellHasCardMoves,
  useBoardItemLabel,
  useBoardLabels,
} from '@github-ui/board-core'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type ReactNode, useMemo} from 'react'

import headingStyles from '../BoardHeadings.module.css'
import {BoardCardSpacer} from '../window/BoardWindow'
import {useBoardCardWindow} from '../window/use-board-card-window'
import {useAnnounceOnce} from './announce-once'
import {BoardCardFrame} from './BoardCardFrame'
import {hasContent} from './BoardCardMenu'
import styles from './BoardCellFrame.module.css'
import {BoardItemPaging} from './BoardPaging'

export type BoardCellFrameProps<TItem> = {
  address: CellAddress
  /**
   * The cards loaded in this cell, in order. They come from the caller because reading
   * them is the one thing that needs the item type, and keeping it here is what lets
   * `useBoardCell` stay free of the generic.
   *
   * Must be the same array `useBoardData` holds for this cell, so it lines up with the
   * ids the hook returns.
   */
  items: readonly TItem[]
  renderCard: (item: TItem) => ReactNode
  /** The caller's own items for one card's menu, above the move controls. See `BoardBody`. */
  renderCardActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  /**
   * What an empty cell shows. When it is not supplied, the `emptyCell` label remains
   * available to assistive technology without drawing visible copy. A renderer that
   * returns `null` suppresses the empty state for a particular cell.
   */
  renderEmptyCell?: (address: CellAddress) => ReactNode
  className?: string
}

/**
 * One cell: the intersection of a column and a row.
 *
 * A pair the caller never supplied and a pair supplied with no items reach this
 * component the same way, so they render identically. That matters because the caller's
 * `cells` is sparse (it holds only the pairs something is in), and a board that drew
 * absent pairs differently would lose a cell every time a column and a row happened not
 * to overlap.
 */
export function BoardCellFrame<TItem>({
  address,
  items,
  renderCard,
  renderCardActions,
  renderEmptyCell,
  className,
}: BoardCellFrameProps<TItem>) {
  const {
    itemIds,
    cellProps,
    bodyProps,
    bodyRef,
    focusedItemIndex,
    isCardFocusPending,
    isEmpty,
    isLoadingItems,
    hasMoreItems,
    cardSetSize,
    heading,
    headingLevel,
    isOver,
    dropIndex,
  } = useBoardCell(address)
  const labels = useBoardLabels()
  const getItemLabel = useBoardItemLabel<TItem>()
  const hasCardMoves = useBoardCellHasCardMoves(address)
  const Heading = `h${headingLevel}` as const

  // A page arriving is a status message: the board pages by being scrolled rather than by
  // anything the reader pressed, so nobody asked for it.
  useAnnounceOnce(labels.loadingItems, isLoadingItems)

  // Only the cards on screen are drawn, plus whichever one the board's focus position
  // names, so a cell holding five hundred costs what fits rather than what it holds.
  const {slots, listRef, measureRef} = useBoardCardWindow({
    bodyRef,
    itemIds,
    focusedIndex: focusedItemIndex,
    isFocusPending: isCardFocusPending,
  })

  /*
    Which drawn card the insertion line goes on, and which of its edges.

    `dropIndex` is a place among the cards the cell has loaded, and only the drawn ones have
    an edge to draw on. The index itself is the usual answer, marked before the card it
    names. One past the last card the cell drew is the other, marked after that card, and
    it is how the end of a partly drawn cell gets a line at all: no card carries that index,
    because there is either no such card or it is one the window left out.
  */
  const insertionAt = useMemo(() => {
    if (dropIndex === null) return null

    const drawn = new Set(slots.flatMap(slot => (slot.type === 'card' ? [slot.index] : [])))
    if (drawn.has(dropIndex)) return {index: dropIndex, side: 'before' as const}
    if (drawn.has(dropIndex - 1)) return {index: dropIndex - 1, side: 'after' as const}

    return null
  }, [dropIndex, slots])

  // The default copy remains available to assistive technology without filling every
  // empty cell with repeated visible text. A renderer that returns `null` has decided
  // this cell says nothing, and falling back there would overrule it, the same reading
  // of a render prop's `null` that `BoardRowFrame` takes.
  const emptyState =
    renderEmptyCell === undefined ? <VisuallyHidden>{labels.emptyCell}</VisuallyHidden> : renderEmptyCell(address)

  return (
    <div {...cellProps} data-drop-target={isOver ? '' : undefined} className={clsx(styles.CellFrame, className)}>
      <div {...bodyProps} data-loading-items={isLoadingItems ? '' : undefined} className={styles.Body}>
        {heading === undefined ? null : <Heading className={headingStyles.HiddenHeading}>{heading}</Heading>}
        {/*
          The list renders whether or not the cell holds cards. "list, 0 items" tells a
          reader more than silence does, and it gives keyboard reordering a stable
          destination in exactly the cells a person most needs to reach.

          Its window draws a run of cards and reserves the height of the rest, so what the
          scrollbar describes is every card the cell has loaded. Each card states where it
          sits and how many there are, because a browser can only count the ones in the
          page: without it a card would be announced as one of twelve rather than as the
          two hundredth of eight hundred. The list container's own count is still the
          drawn one -- nothing can state a size on a list -- so the honest number is
          carried by the cell's name instead, which a reader hears on the way in, before
          the list states the window's count. Both come from `cardSetSize`, so the number
          in the name and the number on the cards are the same number.
        */}
        <ul ref={listRef} className={styles.Cards}>
          {slots.map(slot => {
            if (slot.type === 'space') {
              return <BoardCardSpacer key={`reserved:${slot.before}`} size={slot.size} />
            }

            const id = itemIds[slot.index]
            const item = items[slot.index]

            return id === undefined || item === undefined ? null : (
              <BoardWindowedCard
                // Both kinds of key are namespaced, because an item id is opaque and may be
                // any string at all, including one that reads exactly like a reservation's.
                key={`card:${id}`}
                itemId={id}
                address={address}
                item={item}
                renderCard={renderCard}
                renderCardActions={renderCardActions}
                // Built here, where the labels are already read, so a card is handed a string
                // and does not wake for every new accessor the caller passes.
                menuLabel={labels.cardActions(getItemLabel(item))}
                hasCardMoves={hasCardMoves}
                index={slot.index}
                setSize={cardSetSize}
                insertion={insertionAt?.index === slot.index ? insertionAt.side : undefined}
                measureRef={measureRef}
              />
            )
          })}
        </ul>
        {/*
          "No cards" is a statement about the whole cell, so it is wrong in a cell that
          has a page waiting: such a cell is not empty, it is unread. `hasMoreItems`
          rather than a count, because the count is a fact the caller stated and may well
          be larger than the nothing that is loaded.
        */}
        {isEmpty && !isLoadingItems && !hasMoreItems ? emptyState : null}
        {/*
          Both halves of "there is more in this cell": the ghost that says so, and the
          loading row that replaces it while a page is in flight. It renders after the
          cards, at the end of what is loaded, because that is the place reaching it is
          supposed to mean, and it holds its room open either way so the swap moves
          nobody.
        */}
        <BoardItemPaging address={address} />
      </div>
      {/*
        The cell's fades, drawn over the cards at whichever block edge still has some past
        it. It sits outside the scrolling body because it has to hold still while that
        scrolls, and after it because the stylesheet reads the body's scroll-edge state
        from here.
      */}
      <div className={styles.ScrollFades} />
    </div>
  )
}

type BoardWindowedCardProps<TItem> = {
  itemId: ItemId
  address: CellAddress
  item: TItem
  renderCard: (item: TItem) => ReactNode
  renderCardActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  menuLabel: string
  hasCardMoves: boolean
  index: number
  setSize: number
  insertion?: 'before' | 'after'
  measureRef: (node: Element | null) => (() => void) | undefined
}

function BoardWindowedCardImpl<TItem>({
  itemId,
  address,
  item,
  renderCard,
  renderCardActions,
  menuLabel,
  hasCardMoves,
  index,
  setSize,
  insertion,
  measureRef,
}: BoardWindowedCardProps<TItem>) {
  const actions = renderCardActions?.(item, {columnKey: address.columnKey, rowKey: address.rowKey, itemId})
  // A card has a menu when it has somewhere to move, because the menu is how a card moves
  // without a pointer, or when the caller gave it actions.
  const hasMenu = hasCardMoves || hasContent(actions)

  return (
    <BoardCardFrame
      itemId={itemId}
      address={address}
      place={{index, setSize, measureRef}}
      insertion={insertion}
      menu={hasMenu ? {label: menuLabel, actions} : undefined}
    >
      {renderCard(item)}
    </BoardCardFrame>
  )
}

/**
 * One drawn card, and the boundary that stops an arrow key redrawing the rest of them.
 *
 * A cell re-renders when the board's focus position moves into or out of it, because the
 * card that position names is one the window has to draw wherever it is. Without a boundary
 * here, that would run the caller's `renderCard` again for every card in the window on
 * every keypress -- and a reader holding an arrow key down is the one moment the board can
 * least afford it.
 *
 * The props are the scalars the cell already has rather than the `place` object, because an
 * object built in the loop would be a new one every render and there would be nothing to
 * compare.
 *
 * `memo` erases the type parameter, so the original signature is put back. It is a
 * restatement of the function's own type rather than a widening of it, which is the
 * standard way to memoise a generic component.
 */
const BoardWindowedCard = memo(BoardWindowedCardImpl) as typeof BoardWindowedCardImpl
